import React from 'react';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { msalConfig } from './authConfig';
import DomainGuard from './DomainGuard';
import LoginPage from './LoginPage';
import EmailController from './controllers/EmailController';
import './index.css';

function isWebCryptoAvailable() {
  try {
    const c = window && (window.crypto || window.msCrypto);
    // Some older WebKit expose webkitSubtle
    return !!(c && (c.subtle || c.webkitSubtle));
  } catch (e) {
    return false;
  }
}

function CryptoMissingNotice() {
  return (
    <div style={{ padding: 24, fontFamily: 'sans-serif' }}>
      <h2>Browser configuration not supported</h2>
      <p>
        This application requires the Web Crypto API (window.crypto.subtle) to be available in the
        browser in order to perform secure authentication flows. Your current environment does not
        provide a compatible crypto object.
      </p>
      <p>Possible fixes:</p>
      <ul>
        <li>Open the app in a modern browser (Chrome, Firefox, Edge, Safari) and ensure it's up to date.</li>
        <li>Serve the app over HTTPS or use <code>http://localhost</code> during development (secure context may be required).</li>
        <li>If you're running inside an older WebView, upgrade the webview or run the app in a full browser.</li>
      </ul>
    </div>
  );
}

let msalInstance = null;
if (isWebCryptoAvailable()) {
  try {
    msalInstance = new PublicClientApplication(msalConfig);
  } catch (e) {
    // If msal still errors, leave instance null to render message below
    // eslint-disable-next-line no-console
    console.error('Failed to initialize MSAL PublicClientApplication', e);
    msalInstance = null;
  }
}

function App() {
  if (!msalInstance) {
    return <CryptoMissingNotice />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <DomainGuard>
          <EmailController />
        </DomainGuard>
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <LoginPage msalInstance={msalInstance} />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}

export default App;